import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { CriminalDocumentRow } from '../../../types/criminalCaseRequest';
import { useCriminalAppellateWizardContext } from '../../../hooks/useCaseRequestWizard';
import { getProfile, type ExtranetUserProfile } from '../../../services/userProfile';
import * as criminalAppellateService from '../../../services/criminalAppellateCaseRequests';
import { setFlashToast } from '../../../utils/flashToast';
import { focusFirstInvalid, validateRequired, type RequiredErrors, type RequiredFieldSpec } from '../../../utils/requiredValidation';
import { formatDisplayDate } from '../../../utils/dateFormat';
import { apiOrLookupLabel } from '../../view-request/viewRequestHelpers';
import { CriminalReviewDocumentsTable } from '../criminal/CriminalReviewDocumentsTable';
import {
  ReviewChips,
  ReviewField,
  ReviewGrid,
  ReviewSection,
  ReviewSubBlock,
} from '../criminal/CriminalReviewUi';
import { CriminalAppellateWizardActions } from './CriminalAppellateWizardActions';
import { criminalAppellateWizardPath } from './CriminalAppellateWizardShell';
import { useCriminalAppellateLookups } from './useCriminalAppellateLookups';
import CriminalAppellateCoverNoteModal from './CriminalAppellateCoverNoteModal';
import { useRole } from '../../../rbac/hooks';
import { tasksListPathForRole } from '../../../rbac/tasksListPath';

export default function CriminalAppellateReviewPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { roleCode } = useRole();
  const tasksListPath = tasksListPathForRole(roleCode);
  const { draft, setDraft } = useCriminalAppellateWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const [profile, setProfile] = useState<ExtranetUserProfile | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showCoverNote, setShowCoverNote] = useState(false);
  const [coverNoteRequestId, setCoverNoteRequestId] = useState('');
  const [coverNoteFileRef, setCoverNoteFileRef] = useState('');
  const lookups = useCriminalAppellateLookups('review', draft.provinceId, draft.districtId);

  useEffect(() => {
    let mounted = true;
    void getProfile().then((r) => {
      if (mounted && r.success) setProfile(r.data);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const appealMatterLabel =
    draft.appealMatter === 'yes' ? 'Yes' : draft.appealMatter === 'no' ? 'No' : '—';

  const connectedLabel =
    draft.hasConnectedMatters === 'yes' ? 'Yes' : draft.hasConnectedMatters === 'no' ? 'No' : '—';

  const clientTypeLabel =
    draft.clientType === 'corporation'
      ? 'Public Corporation / Statutory Board / State Owned Entity / Other Incorporated Body'
      : 'Government Ministry / Department / Other Unincorporated Body';

  const deliveryLabel = useMemo(() => {
    if (!draft.deliveryMethods.length) return '—';
    return draft.deliveryMethods
      .map((name) => {
        if (name.toLowerCase().includes('other') && draft.deliveryOther?.trim()) {
          return `${name} - ${draft.deliveryOther.trim()}`;
        }
        return name;
      })
      .join(', ');
  }, [draft.deliveryMethods, draft.deliveryOther]);

  const editTo = (step: 1 | 2 | 3 | 4 | 5) =>
    criminalAppellateWizardPath(step, draft.draftRequestId);

  const submit = async () => {
    if (submitting) return;

    const specs: RequiredFieldSpec[] = [
      {
        key: 'confirmCorrect',
        label: 'Confirmation',
        value: draft.confirmCorrect ? 'yes' : '',
        elementId: 'capp-confirm',
      },
    ];
    const reqErrs = validateRequired(specs);
    setErrors(reqErrs);
    if (Object.keys(reqErrs).length) {
      focusFirstInvalid(specs, reqErrs);
      return;
    }

    setSubmitting(true);
    const r = await criminalAppellateService.saveCriminalAppellateRequest(draft, {
      isDraft: false,
      confirmedCorrect: true,
    });
    if (!r.success) {
      setSubmitting(false);
      setFlashToast({ message: r.message ?? 'Submission failed.', variant: 'error' });
      return;
    }

    const returnedId = r.data.requestId || draft.draftRequestId;
    const returnedRef = r.data.fileReferenceNumber || draft.referenceNo;

    setDraft((d) => ({
      ...d,
      draftRequestId: returnedId || d.draftRequestId,
      referenceNo: returnedRef || d.referenceNo,
    }));
    setFlashToast({ message: 'Case Request submitted successfully.', variant: 'success' });
    setCoverNoteRequestId(returnedId || draft.draftRequestId);
    setCoverNoteFileRef(returnedRef || draft.referenceNo || '');
    setShowCoverNote(true);
  };

  const closeCoverNote = () => {
    setShowCoverNote(false);
    setCoverNoteRequestId('');
    setCoverNoteFileRef('');
    nav(tasksListPath);
  };

  return (
    <>
      <CriminalAppellateCoverNoteModal
        isOpen={showCoverNote}
        caseRequestId={coverNoteRequestId}
        fileRefFallback={coverNoteFileRef}
        showSuccessBanner
        onClose={closeCoverNote}
      />

      <div className="ccr-review-page">
        <div className="bc">
          <Link to={tasksListPath}>{t('nav.tasksAndCases')}</Link>
          <span className="bc-sep">/</span>
          <Link to={editTo(1)}>Create Case Request</Link>
          <span className="bc-sep">/</span>
          Review Before Submission
        </div>
        <div className="pg-title">Review Case Request Details</div>

        {/* ── Step 01 — Case Overview ── */}
        <ReviewSection step="Step 01" title="Case Overview" editTo={editTo(1)}>
          <ReviewSubBlock title="Case Intake Details">
            <ReviewGrid cols={4}>
              <ReviewField label="File Category" value={draft.fileCategory} />
              <ReviewField label="Sub File Category" value={draft.subFileCategoryName} />
            </ReviewGrid>
            <ReviewChips items={draft.applicableAreas} />
            <ReviewField label="Description" value={draft.description} fullWidth />
            <ReviewGrid cols={4}>
              <ReviewField label="Appeal Matter" value={appealMatterLabel} />
              {draft.appealMatter === 'yes' ? (
                <>
                  <ReviewField label="Original Court" value={draft.originalCourt} />
                  <ReviewField
                    label="Judgement / Order"
                    value={draft.judgementOrderOriginalFileName || draft.judgementOrderFileName}
                  />
                </>
              ) : null}
              <ReviewField
                label="Province"
                value={apiOrLookupLabel(draft.provinceName, draft.provinceId, lookups.provinces)}
              />
              <ReviewField
                label="District"
                value={apiOrLookupLabel(draft.districtName, draft.districtId, lookups.districts)}
              />
              <ReviewField
                label="Police Station"
                value={apiOrLookupLabel(
                  draft.policeStationName,
                  draft.policeStationId,
                  lookups.policeStations,
                )}
              />
              <ReviewField
                label="Your Reference Number (External)"
                value={draft.yourReferenceNumber}
              />
              <ReviewField
                label="Other External Reference Number(s)"
                value={draft.otherExternalReferences}
              />
              <ReviewField label="Case Number" value={draft.caseNumber} />
              <ReviewField
                label="Are there any connected matters (Cases)?"
                value={connectedLabel}
              />
              {draft.hasConnectedMatters === 'yes' ? (
                <>
                  <ReviewField label="Connected Case Number" value={draft.connectedCaseNumber} />
                  <ReviewField
                    label="Connected AGD File Reference Number"
                    value={draft.connectedAgdFileRef}
                  />
                </>
              ) : null}
              {draft.instituteType === 'corporation' && draft.corporateInstitute ? (
                <>
                  <ReviewField
                    label="Corporate Institute"
                    value={draft.corporateInstituteName || draft.corporateInstitute}
                  />
                  {draft.corporateInstituteOther ? (
                    <ReviewField
                      label="Other Institute Name"
                      value={draft.corporateInstituteOther}
                    />
                  ) : null}
                </>
              ) : null}
            </ReviewGrid>
          </ReviewSubBlock>
        </ReviewSection>

        {/* ── Step 02 — Party Details ── */}
        <ReviewSection step="Step 02" title="Party Details" editTo={editTo(2)}>
          <ReviewSubBlock title="Party Details">
            <ReviewGrid cols={4}>
              <ReviewField
                label="Plaintiff / Applicant Name"
                value={draft.plaintiffApplicantName}
              />
              <ReviewField
                label="Defendant / Respondent Name"
                value={draft.defendantRespondentName}
              />
              <ReviewField label="Select one of the following" value={clientTypeLabel} span={2} />
              {draft.otherInvolvedInstitutions ? (
                <ReviewField
                  label="Other Involved Institutions"
                  value={draft.otherInvolvedInstitutions}
                  fullWidth
                />
              ) : null}
              <ReviewField
                label="Name of the Legal Officer / Officer In-Charge"
                value={draft.legalOfficerName}
              />
              <ReviewField label="Contact Number" value={draft.legalOfficerContact} />
            </ReviewGrid>
          </ReviewSubBlock>
        </ReviewSection>

        {/* ── Step 03 — Timeline ── */}
        <ReviewSection step="Step 03" title="Timeline" editTo={editTo(3)}>
          <ReviewSubBlock title="Timeline">
            <ReviewGrid cols={3}>
              <ReviewField
                label="Urgency Level"
                value={apiOrLookupLabel(
                  draft.urgencyLevelName,
                  draft.urgencyLevelId,
                  lookups.urgencyLevels,
                )}
              />
              <ReviewField label="Deadline" value={formatDisplayDate(draft.deadline)} />
              <ReviewField label="Urgency Justification" value={draft.urgencyJustification} />
            </ReviewGrid>
          </ReviewSubBlock>
        </ReviewSection>

        {/* ── Step 04 — Submission & Delivery ── */}
        <ReviewSection step="Step 04" title="Submission & Physical File Delivery" editTo={editTo(4)}>
          <ReviewSubBlock title="Request Submitter's Details">
            <ReviewGrid cols={4}>
              <ReviewField
                label="Submitted Division/Unit"
                value={profile?.divisionUnit?.trim() || '—'}
              />
              <ReviewField label="Submitted by" value={profile?.displayName?.trim() || '—'} />
              <ReviewField
                label="Designation"
                value={profile?.contactDesignation?.trim() || '—'}
              />
              <ReviewField label="Email Address" value={profile?.email?.trim() || '—'} />
              <ReviewField label="Contact Number" value={profile?.contactNumber?.trim() || '—'} />
            </ReviewGrid>
          </ReviewSubBlock>
          <ReviewSubBlock title="Physical File Delivery Method">
            <ReviewField
              label="Physical File Delivery Method"
              value={deliveryLabel}
              fullWidth
            />
          </ReviewSubBlock>
        </ReviewSection>

        {/* ── Step 05 — Upload Documents ── */}
        <ReviewSection step="Step 05" title="Upload Documents" editTo={editTo(5)}>
          <ReviewSubBlock>
            <CriminalReviewDocumentsTable
              documents={draft.documents as unknown as CriminalDocumentRow[]}
            />
          </ReviewSubBlock>
        </ReviewSection>

        {/* ── Confirmation ── */}
        <div className="confirm-section ccr-review-confirm">
          <label className="ccr-chk" id="capp-confirm">
            <input
              type="checkbox"
              checked={draft.confirmCorrect}
              disabled={submitting}
              onChange={(e) => {
                setDraft((d) => ({ ...d, confirmCorrect: e.target.checked }));
                if (e.target.checked) setErrors((er) => ({ ...er, confirmCorrect: '' }));
              }}
            />
            <span className="ccr-review-confirm-text">
              <span className="ccr-review-confirm-main">
                I confirm the above information is correct <span className="req">*</span>
              </span>
              <span className="ccr-review-confirm-hint">
                Please confirm before submitting this request to AGD.
              </span>
            </span>
          </label>
          {errors.confirmCorrect ? (
            <div className="ccr-review-confirm-err">{errors.confirmCorrect}</div>
          ) : null}
        </div>

        <CriminalAppellateWizardActions
          draft={draft}
          setDraft={setDraft}
          currentStep={5}
          onBack={() => nav(editTo(5))}
          showSubmit
          submitting={submitting}
          onSubmit={() => void submit()}
          reviewMode
        />
      </div>
    </>
  );
}
